import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/industries`; // Change to your backend URL
const PRODUCTS_API = `${process.env.REACT_APP_API_URL}/products`;

const IndustriesPage = () => {
  const [industries, setIndustries] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    fetchIndustries();
    fetchProducts();
  }, []);

  const fetchIndustries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/`);
      if (!response.ok) throw new Error("Failed to fetch industries");
      const data = await response.json();
      const industriesData = data.data || data;
      // console.log("industry", industriesData);
      setIndustries(industriesData);
      if (industriesData.length > 0) {
        setSelectedIndustry(null); // Show all by default
      }
    } catch (err) {
      setError(err.message);
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${PRODUCTS_API}/getAll`);
      if (!response.ok) throw new Error("Failed to fetch products");
      const data = await response.json();
      setProducts(data.data || data);
      // console.log("first",  data.data)
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  const getProductName = (productId) => {
    const product = products.find((p) => p._id === productId);
    // console.log("product" , product);
    return product?.heading || "Unknown Product";
  };

  const displayedIndustries = selectedIndustry
    ? industries.filter((ind) => ind._id === selectedIndustry)
    : industries;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Your Company</h1>

            {/* Dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
              >
                <span>
                  {selectedIndustry
                    ? industries.find((ind) => ind._id === selectedIndustry)
                        ?.title || "Select Industry"
                    : "All Industries"}
                </span>
                <ChevronDown
                  size={20}
                  className={`transition ${dropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <button
                    onClick={() => {
                      setSelectedIndustry(null);
                      setDropdownOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-3 hover:bg-gray-100 border-b border-gray-200 ${
                      !selectedIndustry
                        ? "bg-blue-50 text-blue-600 font-semibold"
                        : "text-gray-900"
                    }`}
                  >
                    All Industries
                  </button>
                  {industries.map((industry) => (
                    <button
                      key={industry._id}
                      onClick={() => {
                        setSelectedIndustry(industry._id);
                        setDropdownOpen(false);
                      }}
                      className={`block w-full text-left px-4 py-3 hover:bg-gray-100 border-b border-gray-200 last:border-0 ${
                        selectedIndustry === industry._id
                          ? "bg-blue-50 text-blue-600 font-semibold"
                          : "text-gray-900"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{industry.icon}</span>
                        <span>{industry.title}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 py-16">
        <div className="max-w-7xl mx-auto px-8 text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-4">
            Industries We Serve
          </h2>
          <p className="text-xl text-gray-600">
            Tailored solutions for diverse industry needs
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 py-16">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-8">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="text-gray-600 mt-4">Loading industries...</p>
          </div>
        ) : displayedIndustries.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-600 text-lg">No industries found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayedIndustries.map((industry) => (
              <IndustryCard
                key={industry._id}
                industry={industry}
                products={products}
                getProductName={getProductName}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Industry Card Component
const IndustryCard = ({ industry, products, getProductName }) => {
  return (
    <div className="bg-white rounded-xl border-4 border-yellow-400 overflow-hidden hover:shadow-xl transition-shadow h-full flex flex-col">
      {/* Card Header with Icon and Metric */}
      <div className="p-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="flex justify-between items-start mb-6">
          {/* Icon */}
          <div className="text-5xl bg-blue-100 p-3 rounded-lg">
            {industry.icon}
          </div>

          {/* Metric */}
          {industry.stats && (
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {industry.stats.metric}
              </div>
              <div className="text-sm text-gray-600 font-medium">
                {industry.stats.label}
              </div>
            </div>
          )}
        </div>

        {/* Title and Description */}
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          {industry.title}
        </h3>
        <p className="text-gray-600 text-sm leading-relaxed">
          {industry.description}
        </p>
      </div>

      {/* Benefits Section */}
      {industry.benefits && industry.benefits.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <ul className="space-y-2">
            {industry.benefits.map((benefit, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span className="text-yellow-500 text-lg font-bold flex-shrink-0 mt-0.5">
                  ✓
                </span>
                <span className="text-gray-700 text-sm">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Core Products Section */}
      {industry.solutions && industry.solutions.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 mt-auto">
          <h4 className="font-semibold text-gray-900 mb-3 text-sm">
            Core Products
          </h4>
          <div className="flex flex-wrap gap-2">
            {industry.solutions.map((solution, idx) => {
              return (
                <Link
                  key={idx}
                  to={`/products/${solution.productId.slug}`}
                  onClick={()=> window.scrollTo({top : 0 , behavior : "smooth"})}
                  className="inline-block px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium border border-blue-200 hover:bg-blue-100 transition cursor-pointer"
                >
                  {solution.productId.heading}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default IndustriesPage;